import { defineStore } from "pinia";
import { isNetworkUnavailable } from "@/composables/network/offlineMode";
import { markRaw } from "vue";
import db from "@/db";
import REPORT_STATUS from "@/db/enums/reportStatus";
import {
  ReportClient,
  generationformclient,
  reportExportRestClient,
} from "@/api";
import { buildQueryset } from "@/utils/queryset";
import { useProjectsStore } from "@/store/modules/projects";
import { useUsersStore } from "@/store/modules/users";
import { useDocumentsStore } from "@/store/modules/documents";
import { useReportTemplatesStore } from "@/store/modules/reportTemplates";
import {
  pollExport,
  pollReport,
  trackGeneratedReport,
} from "@/composables/reports/useReportPolling";
import { toReportFormPayload } from "@/composables/reportSteppers/reportFormPayload";
import omit from "lodash/omit";
import { OfflineError } from "@/utils/errors";
import { choices as projectChoices } from "@/composables/choices/projectChoices";
import { DATA_EXPORT_TYPES } from "@/db/enums/reportTypes";
import { logRequestFailure } from "@/utils/network";

const Report = db.models.Report;

const EXPORT_FAILURE_STATUS = "FAILURE";
const EXPORT_FALLBACK_NAME = "Export QAL2/AST";

let lastReportFilter = null;

const projectHasExcelExports = () =>
  (useProjectsStore().currentProject?.controlTypes ?? []).includes(
    projectChoices.QAL2_AST
  );

const belongsToCurrentProject = (report) =>
  !!report && report.objectId === useProjectsStore().currentProjectUuid;

const excelExportRow = (fields) => ({
  documentUuid: null,
  generationError: null,
  chronoNumber: "",
  revision: "",
  isExcelExport: true,
  ...fields,
});

const pendingGenerationFormSaves = new Map();
const savedGenerationFormSignatures = new Map();

export const useReportsStore = defineStore("reports", {
  state: () => ({
    reportList: [],
    totalReports: 0,
    reportIsLoading: false,
    reportIsFetching: false,
    currentGenerationData: null,
    loadingGenerationData: false,
    loadingPreviewParameter: false,
    parameterPreviewData: {},
    parameterPreviewError: null,
    parameterPreviewSignature: null,
    generationForms: [],
    generationFormsProjectUuid: null,
    generationFormsIsLoading: false,
  }),
  actions: {
    async fetchReports({ serviceId, projectUuid }) {
      if (isNetworkUnavailable()) return;
      if (projectHasExcelExports())
        this.refreshExcelExports({ serviceId, projectUuid }).catch(
          logRequestFailure
        );
      const isFirstLoad = !this.reportList.length;
      this.reportIsFetching = true;
      if (isFirstLoad) this.reportIsLoading = true;

      try {
        const response = await ReportClient.fullReportList({
          service_id: serviceId,
          object_id: projectUuid,
        });
        try {
          await db.transaction("rw", db.reports, async () => {
            for (const report of response) {
              Report.createOrUpdate({
                ...report,
                revision: report.reportGenerationData.report
                  ? report.reportGenerationData.report.revision_reason
                  : "",
              });
            }
          });
          this.totalReports += response.length;
        } catch (error) {
          console.error(error.stack);
        }
      } finally {
        this.reportIsFetching = false;
        if (isFirstLoad) this.reportIsLoading = false;
      }
    },

    async refreshExcelExports({ serviceId, projectUuid }) {
      await this.syncExcelExports({ serviceId, projectUuid });
      if (lastReportFilter?.projectUuid === projectUuid)
        await this.filterReports(lastReportFilter);
    },

    async syncExcelExports({ serviceId, projectUuid }) {
      if (useProjectsStore().isOfflineState) return;
      const syncStartedAt = new Date();
      try {
        const reportTemplatesStore = useReportTemplatesStore();
        if (!reportTemplatesStore.reportTemplateList.length) {
          await reportTemplatesStore.fetchReportTemplates(serviceId);
        }
        const dataExportTemplateUuid =
          reportTemplatesStore.reportTemplateList.find(({ code }) =>
            DATA_EXPORT_TYPES.has(code)
          )?.uuid;
        const runningExports = await reportExportRestClient
          .listQal2AstExports(projectUuid)
          .catch((error) => {
            console.error(error);
            return null;
          });
        const exports =
          await useDocumentsStore().fetchQal2AstExports(projectUuid);
        const rows = [
          ...exports.map((document) => {
            const exportedAt = new Date(document.datetime).toISOString();
            return excelExportRow({
              uuid: document.uuid,
              objectId: projectUuid,
              reportName: document.name,
              template: dataExportTemplateUuid,
              status: REPORT_STATUS.EXPORTED,
              author: document.metaDatas?.author ?? "",
              documentUuid: document.uuid,
              createdAt: exportedAt,
              updatedAt: exportedAt,
            });
          }),
          ...(runningExports ?? []).map((task) =>
            excelExportRow({
              uuid: task.taskId,
              objectId: projectUuid,
              reportName: task.name ?? EXPORT_FALLBACK_NAME,
              template: dataExportTemplateUuid,
              status:
                task.status === EXPORT_FAILURE_STATUS
                  ? REPORT_STATUS.ERROR
                  : REPORT_STATUS.GENERATING,
              generationError: task.detail ?? null,
              author: task.author ?? "",
              createdAt: task.createdAt,
              updatedAt: task.createdAt,
            })
          ),
        ];
        const rowUuids = new Set(rows.map(({ uuid }) => uuid));
        await db.transaction("rw", db.reports, async () => {
          await db.reports
            .where("objectId")
            .equals(projectUuid)
            .filter(
              (report) =>
                report.isExcelExport &&
                !rowUuids.has(report.uuid) &&
                new Date(report.updatedAt) < syncStartedAt &&
                (runningExports !== null ||
                  report.status === REPORT_STATUS.EXPORTED)
            )
            .delete();
          for (const row of rows) await Report.createOrUpdate(row);
        });
      } catch (error) {
        if (!(error instanceof OfflineError)) console.error(error);
      }
    },

    async filterReports({
      queryAsObject,
      ordering,
      filterMapping,
      projectUuid,
    }) {
      lastReportFilter = {
        queryAsObject,
        ordering,
        filterMapping,
        projectUuid,
      };
      while (this.reportIsFetching)
        await new Promise((r) => setTimeout(r, 200));

      const isFirstLoad = !this.reportList.length;
      this.reportIsFetching = true;
      if (isFirstLoad) this.reportIsLoading = true;

      try {
        const { queryset, count } = await buildQueryset(
          db.reports,
          queryAsObject,
          ordering,
          filterMapping
        );

        const reports = await queryset.toArray();

        this.reportList = reports.filter(
          (report) => report.objectId === projectUuid
        );
        this.totalReports = count;
      } finally {
        this.reportIsFetching = false;
        if (isFirstLoad) this.reportIsLoading = false;
      }
    },

    async createReport(data) {
      const {
        uuid,
        status,
        chrono_number: chronoNumber,
      } = await ReportClient.create(data);

      await Report.createOrUpdate({
        uuid,
        status,
        chronoNumber,
        objectId: data.projectUuid,
        template: data.templateUuid,
        version: data.version ?? 1,
        author: useUsersStore().currentUser?.email ?? "",
        createdAt: new Date().toISOString(),
        revision: "",
      });
      const reportInstance = await db.reports.get({ uuid });

      if (data.replaceReportUuid) {
        this.replaceReport(data.replaceReportUuid, reportInstance);
      } else {
        this.upsertReport(reportInstance);
        this.totalReports += 1;
      }

      trackGeneratedReport(uuid);
      pollReport(uuid);

      return reportInstance;
    },

    async startQal2AstExport({ templateUuid, ...data }) {
      if (useProjectsStore().isOfflineState) throw new OfflineError();
      const { taskId, name } =
        await reportExportRestClient.startQal2AstExport(data);
      const startedAt = new Date().toISOString();
      await Report.createOrUpdate(
        excelExportRow({
          uuid: taskId,
          objectId: data.projectUuid,
          reportName: name ?? EXPORT_FALLBACK_NAME,
          template: templateUuid,
          status: REPORT_STATUS.GENERATING,
          author: useUsersStore().currentUser?.email ?? "",
          createdAt: startedAt,
          updatedAt: startedAt,
        })
      );
      this.upsertReport(await db.reports.get({ uuid: taskId }));
      this.totalReports += 1;

      trackGeneratedReport(taskId);
      pollExport(taskId);

      return taskId;
    },

    async retrieveReportGenerationData(reportUuid) {
      this.loadingGenerationData = true;
      try {
        this.currentGenerationData =
          await ReportClient.retrieveReportGenerationData(reportUuid);
      } finally {
        this.loadingGenerationData = false;
      }
    },

    resetReportGenerationData() {
      this.loadingGenerationData = true;
      this.currentGenerationData = null;
      this.loadingGenerationData = false;
    },

    async completeExcelExport(taskId, { documentUuid, name }) {
      const running = await db.reports.get({ uuid: taskId });
      await this.forgetReport(taskId);
      if (!running) return;
      await Report.createOrUpdate(
        excelExportRow({
          uuid: documentUuid,
          objectId: running.objectId,
          reportName: name,
          template: running.template,
          status: REPORT_STATUS.EXPORTED,
          author: running.author,
          documentUuid,
          createdAt: running.createdAt,
          updatedAt: new Date().toISOString(),
        })
      );
      if (!belongsToCurrentProject(running)) return;
      const isListed = this.reportList.some(
        ({ uuid }) => uuid === documentUuid
      );
      this.upsertReport(await db.reports.get({ uuid: documentUuid }));
      if (!isListed) this.totalReports += 1;
    },

    async destroyExcelExport({ uuid, status, objectId }) {
      if (status === REPORT_STATUS.EXPORTED)
        await useDocumentsStore().removeDocument(uuid);
      else await reportExportRestClient.destroyQal2AstExport(uuid, objectId);
      await this.forgetReport(uuid);
    },

    async forgetReport(reportUuid) {
      await db.reports.delete(reportUuid);
      const remaining = this.reportList.filter(
        ({ uuid }) => uuid !== reportUuid
      );
      if (remaining.length !== this.reportList.length) this.totalReports -= 1;
      this.reportList = remaining;
    },

    async destroyReport(reportUuid) {
      await ReportClient.destroy(reportUuid);
      await this.forgetReport(reportUuid);
    },

    async validateReport(report) {
      this.reportIsLoading = true;
      try {
        const response = await ReportClient.updateStatus(
          report.uuid,
          REPORT_STATUS.READY
        );
        await Report.createOrUpdate(response);
        this.upsertReport(response);
      } finally {
        this.reportIsLoading = false;
      }
    },

    async invalidateReport(report) {
      this.reportIsLoading = true;
      try {
        const response = await ReportClient.updateStatus(
          report.uuid,
          REPORT_STATUS.NOT_VALIDATED
        );
        await Report.createOrUpdate(response);
        this.upsertReport(response);
      } finally {
        this.reportIsLoading = false;
      }
    },

    async sendReport(report) {
      const response = await ReportClient.updateStatus(
        report.uuid,
        REPORT_STATUS.SENT
      );
      await Report.createOrUpdate(response);
      this.upsertReport(response);
      this.reportIsLoading = false;
    },

    async getFullReport({ serviceId, projectUuid, templateId }) {
      if (isNetworkUnavailable()) throw new OfflineError();
      this.reportIsLoading = true;

      try {
        const response = await ReportClient.fullReportList({
          service_id: serviceId,
          object_id: projectUuid,
          template: templateId,
          page_size: 100,
        });

        return response;
      } finally {
        this.reportIsLoading = false;
      }
    },

    async previewParameter(dataToDisplayForm, { signature, force } = {}) {
      const isUpToDate =
        !force &&
        signature &&
        signature === this.parameterPreviewSignature &&
        (!!this.parameterPreviewData.result_list ||
          !!this.parameterPreviewError);
      if (isUpToDate) return;

      this.parameterPreviewData = {};
      this.parameterPreviewError = null;
      this.parameterPreviewSignature = null;

      if (isNetworkUnavailable()) {
        this.parameterPreviewError = new OfflineError();
        this.parameterPreviewSignature = signature ?? null;
        return;
      }

      this.loadingPreviewParameter = true;
      try {
        this.parameterPreviewData =
          await ReportClient.queryParameterPreviewData(dataToDisplayForm);
      } catch (error) {
        console.error(error);
        this.parameterPreviewError = markRaw(error);
      } finally {
        this.parameterPreviewSignature = signature ?? null;
        this.loadingPreviewParameter = false;
      }
    },

    async createGenerationForm(form) {
      if (isNetworkUnavailable()) throw new OfflineError();
      this.generationFormsIsLoading = true;
      const response = await generationformclient.create({
        project: useProjectsStore().currentProjectUuid,
        ...form,
      });
      this.generationForms = [omit(response, "form"), ...this.generationForms];
      this.generationFormsIsLoading = false;
      return response;
    },

    async retrieveGenerationForm(uuid) {
      return generationformclient.retrieve(uuid);
    },

    async fetchGenerationForms(projectUuid) {
      if (isNetworkUnavailable()) return;
      this.generationFormsIsLoading = true;
      const response = await generationformclient.list({
        project: projectUuid,
        ordering: "-created_at",
        page_size: "100",
        omit_form: "true",
      });
      this.generationForms = response.results;
      this.generationFormsProjectUuid = projectUuid;
      this.generationFormsIsLoading = false;
      return response;
    },

    updateGenerationForm(form, { skipIfUnchanged = false } = {}) {
      if (isNetworkUnavailable()) return Promise.reject(new OfflineError());
      const signature = JSON.stringify(toReportFormPayload(form));
      const previousSave = pendingGenerationFormSaves.get(form.uuid);
      const save = (previousSave || Promise.resolve())
        .catch(() => {})
        .then(() =>
          skipIfUnchanged &&
          savedGenerationFormSignatures.get(form.uuid) === signature
            ? form
            : this.saveGenerationForm(form, signature)
        );
      pendingGenerationFormSaves.set(form.uuid, save);
      const cleanUp = () => {
        if (pendingGenerationFormSaves.get(form.uuid) === save)
          pendingGenerationFormSaves.delete(form.uuid);
      };
      save.then(cleanUp, cleanUp);
      return save;
    },

    async saveGenerationForm(form, signature) {
      this.generationFormsIsLoading = true;
      try {
        const saved = await generationformclient.updateWithoutFormEcho(
          form,
          form.uuid
        );
        savedGenerationFormSignatures.set(form.uuid, signature);
        this.replaceGenerationForm(saved);
        return saved;
      } finally {
        this.generationFormsIsLoading = false;
      }
    },

    async deleteGenerationForm(uuid) {
      if (isNetworkUnavailable()) throw new OfflineError();
      this.generationFormsIsLoading = true;
      await generationformclient.destroy(uuid);
      this.generationForms = this.generationForms.filter(
        (form) => form.uuid !== uuid
      );
      this.generationFormsIsLoading = false;
    },

    async duplicateGenerationForm(reportUuid) {
      if (isNetworkUnavailable()) throw new OfflineError();
      this.generationFormsIsLoading = true;
      const response = await generationformclient.duplicate(reportUuid);
      this.generationForms = [omit(response, "form"), ...this.generationForms];
      this.generationFormsIsLoading = false;
      return response;
    },

    replaceReport(uuid, report) {
      const index = this.reportList.findIndex((item) => item.uuid === uuid);
      if (index < 0) this.reportList.unshift(report);
      else this.reportList.splice(index, 1, report);
    },

    upsertListedReport(report) {
      if (belongsToCurrentProject(report)) this.upsertReport(report);
    },

    upsertReport(report) {
      const index = this.reportList.findIndex(
        ({ uuid }) => report.uuid === uuid
      );

      if (index < 0) {
        this.reportList.unshift(report);
      } else {
        this.reportList.splice(index, 1, report);
      }
    },

    replaceGenerationForm(generationForm) {
      const form = omit(generationForm, "form");
      if (form.isArchived) {
        this.generationForms = this.generationForms.filter(
          ({ uuid }) => uuid !== form.uuid
        );
        return;
      }
      const index = this.generationForms.findIndex(
        ({ uuid }) => uuid === form.uuid
      );
      if (index >= 0) {
        this.generationForms.splice(index, 1, form);
      } else {
        this.generationForms = [form, ...this.generationForms];
      }
    },
  },
});
